import { useState } from 'react';
import { formatTime, getPhaseTypeLabel, getPhaseTypeColor, getTimeDiff } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Mic, Radio, MessageSquare, FileText, Play, Music, CornerDownRight, Info } from 'lucide-react';
import ResourcePreviewModal from './ResourcePreviewModal';

const SpeakerAvatar = ({ speaker, microphone }) => {
  if (!speaker) return null;
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium overflow-hidden">
            {speaker.avatar_url ? (
              <img src={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" />
            ) : (
              speaker.initials
            )}
          </div>
          <span className="text-sm truncate max-w-[70px]">{speaker.name.split(' ')[0]}</span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="top">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            {speaker.avatar_url ? (
              <img src={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-muted-foreground">{speaker.initials}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold">{speaker.name}</h4>
            {speaker.role && <p className="text-sm text-muted-foreground">{speaker.role}</p>}
            {microphone ? (
              <div className="flex items-center gap-1 mt-2 text-xs">
                <Mic className="w-3 h-3 text-red-500" />
                <span className="text-red-500 font-medium">{microphone.name}</span>
                {microphone.channel && <span className="text-muted-foreground">({microphone.channel})</span>}
              </div>
            ) : (
              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                <Radio className="w-3 h-3" />
                <span>Sin micrófono asignado</span>
              </div>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export const ClientPhaseRow = ({
  phase,
  index,
  isActive,
  isNext,
  phaseElapsed,
  speakers,
  microphones = [],
  screens = [],
  gridTemplate = '1fr 120px 140px 100px 64px 64px 64px 64px 44px',
  commentCount = 0,
  onCommentClick,
  isSubPhase = false,
  phaseStartTime,
}) => {
  const [preview, setPreview] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const phaseSpeakers = (phase.speaker_ids || [])
    .map(id => speakers.find(s => s.id === id))
    .filter(Boolean);

  if (phaseSpeakers.length === 0 && phase.speaker_id) {
    const legacySpeaker = speakers.find(s => s.id === phase.speaker_id);
    if (legacySpeaker) phaseSpeakers.push(legacySpeaker);
  }

  const getSpeakerMic = (speakerId) => microphones.find(m => m.assigned_to === speakerId);
  const diff = getTimeDiff(phase.duration_seconds, phaseElapsed);
  const progress = Math.min(100, (phaseElapsed / phase.duration_seconds) * 100);

  const getStatusClass = () => {
    if (phase.status === 'completed') return 'bg-green-500';
    if (isActive) return 'bg-red-500 animate-pulse';
    if (isNext) return 'bg-green-500/50';
    return 'bg-zinc-600';
  };

  const getRowClass = () => {
    if (isActive) return 'phase-active';
    if (isNext) return 'phase-next';
    return '';
  };

  return (
    <div
      className={`phase-row relative grid gap-2 items-center px-3 py-3 border-b border-border/40 ${getRowClass()}`}
      style={{ gridTemplateColumns: gridTemplate }}
      data-testid={`client-phase-row-${phase.id}`}
    >
      {/* Status strip */}
      <div className={`status-strip ${getStatusClass()}`} style={{ backgroundColor: isActive || isNext ? undefined : phase.color }} />

      {/* Name & Description */}
      <div className={`min-w-0 ${isSubPhase ? 'pl-5 border-l-2 border-muted ml-1' : ''}`}>
        {isSubPhase && <CornerDownRight className="w-3 h-3 text-muted-foreground mb-0.5 inline-block mr-1 opacity-50" />}
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {isActive && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 uppercase tracking-widest animate-pulse-live">
              En vivo
            </Badge>
          )}
          {isNext && !isActive && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 uppercase tracking-widest border-green-500/50 text-green-500">
              Siguiente
            </Badge>
          )}
          <Badge
            variant="outline"
            className="text-[10px] uppercase tracking-widest"
            style={{ borderColor: getPhaseTypeColor(phase.phase_type), color: getPhaseTypeColor(phase.phase_type) }}
          >
            {getPhaseTypeLabel(phase.phase_type)}
          </Badge>
        </div>
        <div className="font-medium truncate">{phase.name}</div>
        {phase.description && (
          <div className="text-xs text-muted-foreground truncate">{phase.description}</div>
        )}
        {(isActive || phase.status === 'completed') && (
          <div className="phase-progress mt-2">
            <div
              className={`phase-progress-bar ${progress > 100 ? 'overtime' : progress > 80 ? 'warning' : 'normal'}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Info / Notes */}
      <div className="min-w-0">
        {phase.notes ? (
          <button
            type="button"
            onClick={() => setNotesOpen(true)}
            className="w-full text-left group flex items-start gap-1 hover:text-foreground transition-colors"
          >
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-xs text-muted-foreground group-hover:text-foreground line-clamp-2 leading-relaxed">
              {phase.notes}
            </span>
          </button>
        ) : (
          <span className="text-muted-foreground opacity-30 text-xs">—</span>
        )}
      </div>

      {/* Notes modal */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              Info — {phase.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">
            {phase.notes}
          </div>
        </DialogContent>
      </Dialog>

      {/* On Stage - Multiple speakers */}
      <div className="flex flex-col gap-1 min-w-0">
        {phaseSpeakers.length > 0 ? (
          phaseSpeakers.map(speaker => (
            <SpeakerAvatar
              key={speaker.id}
              speaker={speaker}
              microphone={getSpeakerMic(speaker.id)}
            />
          ))
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </div>

      {/* Microphones */}
      <div className="text-sm min-w-0">
        {phaseSpeakers.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {phaseSpeakers.map(speaker => {
              const mic = getSpeakerMic(speaker.id);
              return mic ? (
                <div key={speaker.id} className="flex items-center gap-1 text-xs">
                  <Mic className="w-3 h-3 text-red-500 flex-shrink-0" />
                  <span className="truncate text-red-500">{mic.name}</span>
                </div>
              ) : (
                <div key={speaker.id} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Radio className="w-3 h-3 text-green-500 flex-shrink-0" />
                  <span className="text-green-500">libre</span>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>

      {/* Recursos — dynamic screen columns */}
      {screens.map(screen => {
        const val = phase.screen_values?.[screen.id];
        const contentType = val?.content_type || (typeof val === 'string' && val ? 'image' : null);
        const contentUrl = val?.content_url || (typeof val === 'string' ? val : '');
        const openPreview = (e) => {
          e.preventDefault();
          setPreview({ content_type: contentType, content_url: contentUrl, name: screen.name });
        };
        return (
          <div key={screen.id} className="flex items-center justify-center">
            {contentUrl ? (
              contentType === 'image' ? (
                <button type="button" onClick={openPreview} className="hover:opacity-80 transition-opacity" title={screen.name}>
                  <img
                    src={contentUrl}
                    alt={screen.name}
                    className="h-10 w-16 object-cover rounded border border-border"
                  />
                </button>
              ) : contentType === 'video' ? (
                <button type="button" onClick={openPreview} className="flex items-center gap-1 text-primary hover:opacity-80 transition-opacity">
                  <Play className="w-4 h-4" />
                  <span className="text-xs truncate max-w-[60px]">Video</span>
                </button>
              ) : contentType === 'audio' ? (
                <button type="button" onClick={openPreview} className="flex items-center gap-1 text-primary hover:opacity-80 transition-opacity">
                  <Music className="w-4 h-4" />
                  <span className="text-xs truncate max-w-[60px]">Audio</span>
                </button>
              ) : (
                <button type="button" onClick={openPreview} className="flex items-center gap-1 text-primary hover:opacity-80 transition-opacity">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs truncate max-w-[60px]">Doc</span>
                </button>
              )
            ) : (
              <span className="text-muted-foreground text-sm">—</span>
            )}
          </div>
        );
      })}

      {/* Clock start time */}
      <div className="min-w-0 text-right font-mono text-sm tabular-nums">
        {phaseStartTime
          ? phaseStartTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false })
          : '—'}
      </div>

      {/* Estimated time */}
      <div className="min-w-0 text-right font-mono text-sm tabular-nums">
        {formatTime(phase.duration_seconds)}
      </div>

      {/* Actual time */}
      <div className="min-w-0 text-right font-mono text-sm tabular-nums">
        {isActive || phase.status === 'completed' ? formatTime(phaseElapsed) : '—'}
      </div>

      {/* Difference */}
      <div className={`min-w-0 text-right font-mono text-sm tabular-nums ${
        diff.status === 'overtime' ? 'text-red-500' :
        diff.status === 'under' ? 'text-green-500' :
        'text-muted-foreground'
      }`}>
        {isActive || phase.status === 'completed' ? diff.label : '—'}
      </div>

      {/* Comment button */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 relative"
          onClick={() => onCommentClick && onCommentClick(phase.id)}
          title="Comentar esta fase"
        >
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          {commentCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-[9px] flex items-center justify-center text-primary-foreground font-bold">
              {commentCount > 9 ? '9+' : commentCount}
            </span>
          )}
        </Button>
      </div>

      {/* Resource preview modal */}
      <ResourcePreviewModal resource={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export default ClientPhaseRow;
